import { useCallback, useState } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BrandLoadingBar } from '@/components/ui/brand-loading-bar'
import type { UploadBatch } from '../../hooks/useAdminMedia'

interface MediaUploadZoneProps {
  onUpload: (files: File[]) => Promise<void>
  uploadProgress: UploadBatch | null
  disabled?: boolean
  disabledMessage?: string
}

export function MediaUploadZone({ onUpload, uploadProgress, disabled, disabledMessage }: MediaUploadZoneProps) {
  const [dragging, setDragging] = useState(false)

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return
      const arr = Array.from(files).filter((f) => f.type.startsWith('image/'))
      if (arr.length === 0) return
      await onUpload(arr)
    },
    [onUpload]
  )

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragging(false)
      void handleFiles(e.dataTransfer.files)
    },
    [handleFiles]
  )

  const isUploading = uploadProgress !== null
  const isDisabled = disabled || isUploading

  return (
    <div className="space-y-2">
      <label
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors',
          dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
          isDisabled && 'pointer-events-none opacity-50'
        )}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Upload className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {isUploading ? 'Uploading…' : disabled && disabledMessage ? disabledMessage : 'Drop images here or click to browse'}
        </p>
        <input
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          disabled={isDisabled}
          onChange={(e) => void handleFiles(e.target.files)}
        />
      </label>

      {uploadProgress && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {uploadProgress.completed < uploadProgress.total
                ? `Uploading ${uploadProgress.completed + 1} of ${uploadProgress.total}…`
                : `${uploadProgress.total} uploaded, waiting for server…`}
            </span>
            <span>{Math.round(uploadProgress.overallPct)}%</span>
          </div>
          <BrandLoadingBar value={uploadProgress.overallPct} height="sm" />
        </div>
      )}
    </div>
  )
}
